import React, { useCallback, useEffect, useRef, useState } from 'react';
import { occurrenceKey } from './layout/occurrence-key';
import { clickSuppressionMark, isDragTailClick, resolveDrop } from './layout/drag-model';
import type { DropResolution } from './layout/drag-model';
import type { CalendarGridView, CalendarLeanEvent } from './types';

const DRAG_THRESHOLD_PX = 4;

export interface DragState {
    event: CalendarLeanEvent;
    key: string;
    targetMs: number | null;
    active: boolean;
    /** Viewport coordinates of the pointer, so the grid can draw a preview
     *  that follows the cursor. */
    pointerX: number;
    pointerY: number;
}

interface Options {
    view: CalendarGridView;
    /**
     * Grid geometry → the timestamp under the pointer. Pure maths against the
     * grid's own bounding rect: no `document` listeners and no
     * `elementFromPoint`, which is what makes this work unchanged inside the
     * extension's shadow root (where the old calendar needed ~150 lines of
     * monkey-patching to hit-test at all).
     */
    resolveTargetAt: (clientX: number, clientY: number, gridRect: DOMRect) => number | null;
    onDrop: (event: CalendarLeanEvent, resolution: DropResolution) => void;
}

export function useCalendarDrag({ view, resolveTargetAt, onDrop }: Options) {
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [drag, setDrag] = useState<DragState | null>(null);
    const originRef = useRef<{ x: number; y: number } | null>(null);
    /** When the last real drag ended — see `consumeDragClick`. */
    const draggedAtRef = useRef<number | null>(null);
    /** A gesture that became a drag and was then cancelled: its release still has to be swallowed. */
    const cancelledWhileActiveRef = useRef(false);

    const beginDrag = useCallback((event: CalendarLeanEvent, e: React.PointerEvent<HTMLElement>) => {
        if (e.button !== 0) return;
        originRef.current = { x: e.clientX, y: e.clientY };
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        setDrag({ event, key: occurrenceKey(event), targetMs: null, active: false, pointerX: e.clientX, pointerY: e.clientY });
    }, []);

    const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const origin = originRef.current;
        if (!drag || !origin || !gridRef.current) return;

        // Below the threshold this is still a click, not a drag.
        const moved = Math.hypot(e.clientX - origin.x, e.clientY - origin.y);
        if (!drag.active && moved <= DRAG_THRESHOLD_PX) return;

        const rect = gridRef.current.getBoundingClientRect();
        setDrag(current => (current ? {
            ...current,
            active: true,
            pointerX: e.clientX,
            pointerY: e.clientY,
            targetMs: resolveTargetAt(e.clientX, e.clientY, rect),
        } : current));
    }, [drag, resolveTargetAt]);

    const endDrag = useCallback(() => {
        // Mark the gesture as spent so the click it ends in is swallowed
        // rather than read as "open this chip" — see `isDragTailClick`.
        //
        // A gesture cancelled with Escape lands here too, with `drag` already torn
        // down, so it is remembered separately. The mark is taken at the RELEASE
        // rather than at the cancel: the click that has to be swallowed is the one
        // ending THIS gesture, whenever the user gets round to letting go, and
        // stamping it when Escape was pressed meant anyone who held the button a
        // moment longer than the suppression window got the chip menu anyway.
        const wasSpentGesture = !!drag?.active || cancelledWhileActiveRef.current;
        cancelledWhileActiveRef.current = false;
        const mark = clickSuppressionMark(wasSpentGesture, Date.now());
        if (mark !== null) draggedAtRef.current = mark;

        if (drag?.active && drag.targetMs != null) {
            onDrop(drag.event, resolveDrop(drag.event, drag.targetMs, view));
        }
        originRef.current = null;
        setDrag(null);
    }, [drag, onDrop, view]);

    /** True when the click being handled is the tail of a drag; answers once. */
    const consumeDragClick = useCallback(() => {
        const at = draggedAtRef.current;
        draggedAtRef.current = null;
        return isDragTailClick(at);
    }, []);

    const cancelDrag = useCallback(() => {
        // A CANCELLED drag still ends in a release, and the browser reports that
        // release as a click on the chip. Without this, pressing Escape aborted the
        // move and then opened the chip — navigating the user off the calendar and
        // out of the month they were working in, the opposite of "forget this".
        //
        // Remembered rather than stamped, because the release has not happened yet:
        // `endDrag` takes the mark when it does. Only a gesture that actually became
        // a drag counts — an unmoved press is an ordinary click and must still open
        // the chip.
        if (drag?.active) cancelledWhileActiveRef.current = true;

        originRef.current = null;
        setDrag(null);
    }, [drag]);

    // Escape must reach the drag wherever the pointer is. The handler used to sit
    // on a plain <div> with no tabIndex, so it never received a key at all — the
    // documented way out of a drag did nothing.
    useEffect(() => {
        if (!drag) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancelDrag(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drag, cancelDrag]);

    return {
        gridRef,
        drag,
        beginDrag,
        consumeDragClick,
        /** Spread onto the scrolling grid container. */
        gridHandlers: {
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: cancelDrag,
            onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Escape') cancelDrag(); },
        },
    };
}
